import React, { useState, useEffect } from 'react';
import {
  Autocomplete,
  Dialog,
  DialogActions,
  DialogContent,
  DialogContentText,
  DialogTitle,
  Button,
  TextField,
  FormHelperText,
  FormControl,
} from '@mui/material';

import config from '../../js/config';
import { getBearerString } from '../../js/jwt_token';


type RASDSTFilterDialogProps = {
  open: boolean;
  onClose: () => void;
  onSaveCallback: (requestBody: any) => void;
  searchParams?: URLSearchParams;
}

const RASDSTsFilterDialog = ({ open, onClose, onSaveCallback, searchParams }: RASDSTFilterDialogProps) => {
  const filterDefaultValues = {
    software_version: [],
    source_dataset: [],
  };

  const [distinctValues, setDistinctValues] = useState(null);
  const [filters, setFilters] = useState(filterDefaultValues);

  // Helper to convert URLSearchParams to filter state
  function paramsToFilters(params: URLSearchParams) {
    return {
      software_version: params.getAll('software_version'),
      source_dataset: params.getAll('source_dataset'),
    };
  }

  // When dialog opens, initialize filters from searchParams if provided
  useEffect(() => {
    if (open) {
      if (searchParams) {
        setFilters(paramsToFilters(searchParams));
      } else {
        setFilters(filterDefaultValues);
      }
    }
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [open, searchParams]);

  useEffect(() => {
    // Fetch distinct values only once
    const fetchDistinctValues = async () => {
      try {
        const response = await fetch(config.data_platform_api_url + '/rasdsts/filter_options', {
          method: 'GET',
          headers: {
            'Content-Type': 'application/json',
            'Authorization': getBearerString()
          }
        });
        const data = await response.json();
        setDistinctValues(data);
      } catch (error) {
        console.error('Failed to fetch distinct values:', error);
      }
    };

    if (open && !distinctValues) {
      fetchDistinctValues();
    }
  }, [open, distinctValues]);


  const handleFilterChange = (field, value) => {
    setFilters(prevFilters => ({
      ...prevFilters,
      [field]: value,
    }));
  };

  const handleReset = () => {

    // Reset the Values
    setFilters(filterDefaultValues);

    // Send the Reset Values (empty dict)
    onSaveCallback({});
  };

  const onSave = () => {
    // Return Empty Body if equal to default values (see js/query_logs.js) to determine to query w/o filters
    if (JSON.stringify(filterDefaultValues) === JSON.stringify(filters)) {
      onSaveCallback({});
      return
    }

    // Else Return Filters
    var ret_filters = {
      ...filters,
    }

    onSaveCallback(ret_filters);
  };

  return (
    <Dialog
      open={open}
      onClose={onClose}
      fullWidth
    >
      <DialogTitle>Set RAS Dataset Filters</DialogTitle>
      <DialogContent>
        {distinctValues ? (
          <>

            <FormControl fullWidth margin="normal">
              <Autocomplete
                multiple
                options={(distinctValues.software_version || []).sort((a, b) => a.localeCompare(b))} // Inline sorting
                value={filters.software_version}
                onChange={(event, value) => handleFilterChange('software_version', value)}
                renderInput={(params) => (
                  <TextField
                    {...params}
                    label="Software Version"
                    variant="outlined"
                    placeholder="Select or type..."
                  />
                )}
              />
              <FormHelperText>Select multiple Software Versions or start typing to filter the options.</FormHelperText>
            </FormControl>

            <FormControl fullWidth margin="normal">
              <Autocomplete
                multiple
                options={(distinctValues.source_dataset || []).sort((a, b) => a.localeCompare(b))} // Inline sorting
                value={filters.source_dataset}
                onChange={(event, value) => handleFilterChange('source_dataset', value)}
                renderInput={(params) => (
                  <TextField
                    {...params}
                    label="Source Dataset"
                    variant="outlined"
                    placeholder="Select or type..."
                  />
                )}
              />
              <FormHelperText>Select multiple Source Datasets or start typing to filter the options.</FormHelperText>
            </FormControl>
          </>
        ) : (
          <DialogContentText>Loading filter options...</DialogContentText>
        )}
      </DialogContent>
      <DialogActions>
        <Button onClick={onSave} color="primary">
          Apply
        </Button>
        <Button onClick={handleReset} color="primary">
          Reset
        </Button>
        <Button onClick={onClose} color="primary">
          Cancel
        </Button>
      </DialogActions>
    </Dialog>
  );
};

export default RASDSTsFilterDialog;
